import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  input,
  viewChild,
} from '@angular/core';
import * as L from 'leaflet';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';

import { CelulaMapa } from '../../interfaces/celula.interface';

const CENTRO_DEFAULT: L.LatLngTuple = [-34.6037, -58.3816];
const ZOOM_MAXIMO = 15;

function escapar(texto: string): string {
  return texto.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

@Component({
  selector: 'app-mapa-celulas',
  standalone: true,
  imports: [TableModule, Tag],
  templateUrl: './mapa-celulas.component.html',
  styleUrl: './mapa-celulas.component.scss',
})
export class MapaCelulasComponent implements AfterViewInit, OnDestroy {
  readonly celulas = input.required<CelulaMapa[]>();

  private readonly mapaRef = viewChild.required<ElementRef<HTMLDivElement>>('mapa');
  private mapa?: L.Map;
  private capa?: L.LayerGroup;
  private readonly marcadores = new Map<string, L.Marker>();

  readonly ubicadas = computed(() => this.celulas().filter((celula) => celula.coordenadas));
  readonly sinUbicar = computed(() => this.celulas().length - this.ubicadas().length);

  constructor() {
    effect(() => {
      const celulas = this.celulas();
      if (this.mapa) {
        this.dibujar(celulas);
      }
    });
  }

  ngAfterViewInit(): void {
    this.mapa = L.map(this.mapaRef().nativeElement, { center: CENTRO_DEFAULT, zoom: 12 });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap',
    }).addTo(this.mapa);
    this.capa = L.layerGroup().addTo(this.mapa);
    this.dibujar(this.celulas());
    setTimeout(() => this.mapa?.invalidateSize());
  }

  ngOnDestroy(): void {
    this.mapa?.remove();
  }

  numero(celula: CelulaMapa): number {
    return this.celulas().indexOf(celula) + 1;
  }

  enfocar(celula: CelulaMapa): void {
    const marcador = this.marcadores.get(celula.id);
    if (!marcador || !this.mapa) {
      return;
    }
    this.mapa.setView(marcador.getLatLng(), ZOOM_MAXIMO);
    marcador.openPopup();
  }

  private dibujar(celulas: CelulaMapa[]): void {
    if (!this.mapa || !this.capa) {
      return;
    }
    this.capa.clearLayers();
    this.marcadores.clear();

    const puntos: L.LatLngTuple[] = [];
    celulas.forEach((celula, index) => {
      if (!celula.coordenadas) {
        return;
      }
      const punto: L.LatLngTuple = [celula.coordenadas.lat, celula.coordenadas.lng];
      puntos.push(punto);
      const marcador = L.marker(punto, {
        icon: L.divIcon({
          className: 'mapa-celulas__pin',
          html: `<span>${index + 1}</span>`,
          iconSize: [30, 30],
          iconAnchor: [15, 30],
          popupAnchor: [0, -28],
        }),
        title: celula.nombreLider,
      }).bindPopup(this.popup(celula, index + 1));
      marcador.addTo(this.capa!);
      this.marcadores.set(celula.id, marcador);
    });

    if (puntos.length === 1) {
      this.mapa.setView(puntos[0], ZOOM_MAXIMO);
    } else if (puntos.length > 1) {
      this.mapa.fitBounds(L.latLngBounds(puntos), { padding: [24, 24], maxZoom: ZOOM_MAXIMO });
    }
  }

  private popup(celula: CelulaMapa, numero: number): string {
    const direccion = [celula.direccion, celula.localidad].filter(Boolean).join(', ');
    const cuando = [celula.dia, celula.horario].filter(Boolean).join(' · ');
    return `
      <strong>${numero}. ${escapar(celula.nombreLider)}</strong><br>
      ${escapar(direccion || 'Sin dirección')}
      ${cuando ? `<br><small>${escapar(cuando)}</small>` : ''}
    `;
  }
}
